// PerformanceVideoPlayer anchors the Video Performance Direction workflow, letting reviewers
// sync translated audio against the master video with parent-driven play/pause, speed, and
// drift-correction controls tied to an external timing source.
"use strict";
import "react-video-seek-slider/styles.css";
import { VideoSeekSlider } from "react-video-seek-slider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Popover, Switch } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import classes from "../../Assets/Styles/VoiceQAScreen.module.css";
import Slider from "@mui/material/Slider";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeMute from "@mui/icons-material/VolumeMute";
import VolumeOff from "@mui/icons-material/VolumeOff";
import VolumeUp from "@mui/icons-material/VolumeUp";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { primaryColor } from "../../utils/constants";
import { PLAYBACK_SPEED_OPTIONS } from "../../utils/playbackSpeedOptions";
import { parseTimestampAsTotalSeconds } from "../../utils/generalFunctions";
import InputMask from "react-input-mask";

/**
 * Props:
 * - source: string
 * - displayAudioTime: (s: string) => void
 * - seekTime: number (seconds)
 * - seekTimeIndex: any
 * - goToPageAccordingTimeAPI?: (timeSeconds: number) => void
 * - syncPreview: boolean
 * - setSyncPreview: (b: boolean) => void
 * - previewVolume?: number (0-2, 1 = unchanged) // dialogue listening volume while previewing
 * - onPreviewVolumeChange?: (v:number) => void
 * - playPausePlayer: "play" | "pause"
 * - manualPauseTick?: number
 * - masterMs?: number
 * - onSyncPlay?: () => void           // NEW: parent plays the audio
 * - onSyncPause?: () => void          // NEW: parent pauses the audio
 * - onSyncSetSpeed?: (rate:number) => void // NEW: parent sets audio speed
 * - onSyncVideoWaiting?: () => void
 * - onSyncVideoReady?: () => void
 * - onVideoElement?: (el: HTMLVideoElement | null) => void // parent drives preview audio from the video clock
 * - onVideoSizeChange?: ({ width, height }) => void
 * - settingsMenu?: boolean // speed and toggles live behind a settings button
 * - subtitles?: { startTime, endTime, text, originalText }[]
 * - showOriginalSubtitles?, showSubtitles?: boolean
 * - toggleOriginalSubtitles?, toggleTranslatedSubtitles?: () => void
 * - originalLanguageDisplay?, translatedLanguageDisplay?: string
 * - skipSeconds?: number // how far the rewind/forward buttons move (default 0.1)
 * - dialogueSkip?: boolean // previous/next dialogue buttons, using the subtitle start times
 * - onSkip?: (timeSeconds: number) => void // called after a skip button seek, in place of
 *   goToPageAccordingTimeAPI, so the parent can skip reloading dialogues already shown
 */

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Detaches the element's media so the browser stops downloading it.
const releaseVideo = (v) => {
  v.pause();
  v.querySelectorAll("source").forEach((source) => source.removeAttribute("src"));
  v.removeAttribute("src");
  v.load();
};

// Speaker button that mutes and unmutes, with a vertical level slider popping
// up above it on hover or keyboard focus, so it takes no room in the bar.
// `max` is 1 for the video and 2 while previewing dialogue audio, where 1
// leaves each line at its own level.
const VolumeControl = ({ value, max, onChange }) => {
  const lastAudibleRef = useRef(value > 0 ? value : max / 2);
  if (value > 0) lastAudibleRef.current = value;

  const isMuted = value <= 0;
  const Icon = isMuted
    ? VolumeOff
    : value < max * 0.25
    ? VolumeMute
    : value < max * 0.6
    ? VolumeDown
    : VolumeUp;
  const percent = Math.round(value * 100);

  return (
    <div className={classes["VolumeControl"]}>
      <button
        type="button"
        className={classes["PlayerIconButton"]}
        aria-label={isMuted ? "Unmute" : "Mute"}
        title={isMuted ? "Unmute" : "Mute"}
        onClick={() => onChange(isMuted ? lastAudibleRef.current : 0)}
      >
        <Icon fontSize="small" />
      </button>
      <div className={classes["VolumePopup"]}>
        <span className={classes["VolumePopupValue"]} aria-hidden="true">
          {percent}%
        </span>
        <Slider
          size="small"
          orientation="vertical"
          value={value}
          onChange={(_, val) => onChange(clamp(Number(val) || 0, 0, max))}
          min={0}
          max={max}
          step={0.05}
          aria-label="Volume"
          aria-valuetext={`${percent}%`}
          className={classes["VolumeSlider"]}
        />
      </div>
    </div>
  );
};

// Sync thresholds (ms)
const SOFT_DRIFT_MS = 80;
const HARD_DRIFT_MS = 300;
const HARD_COOLDOWN_MS = 800;

const SUBTITLE_OVERLAY_STYLE = {
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  maxWidth: "80%",
  padding: "8px 16px",
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  color: "#fff",
  borderRadius: "var(--radius-md)",
  textAlign: "center",
  fontSize: "var(--font-size-header)",
  zIndex: 10,
  textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
  pointerEvents: "none",
};

// Subtitle text size (px) and max width (% of the video) in the settings
// menu; the defaults match the overlay's original look.
const SUBTITLE_FONT_SIZE = { min: 12, max: 40, default: 24 };
const SUBTITLE_MAX_WIDTH = { min: 30, max: 100, default: 80 };

// Remembered per browser; storage can be unavailable (private windows).
const readStoredNumber = (key, { min, max, default: fallback }) => {
  try {
    const value = Number(window.localStorage.getItem(key));
    return value >= min && value <= max ? value : fallback;
  } catch (error) {
    return fallback;
  }
};

const storeNumber = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch (error) {
    // Not remembered this time; the setting still applies.
  }
};

// Being further than this into a dialogue makes "previous dialogue" restart it
// instead of going to the one before.
const DIALOGUE_RESTART_THRESHOLD_SEC = 1;

// Wait this long after the last slider movement before loading that time's dialogue page
const SLIDER_PAGE_JUMP_DELAY_MS = 400;

const PerformanceVideoPlayer = (props) => {
  const player = useRef(null);
  const lastHardSyncAt = useRef(0);
  const { onVideoElement } = props;
  const setPlayerElement = useCallback(
    (element) => {
      player.current = element;
      onVideoElement?.(element);
    },
    [onVideoElement]
  );

  const [currentTime, setCurrentTime] = useState(0); // ms
  const [progress, setProgress] = useState(0);       // ms buffered
  const [maxTime, setMaxTime] = useState(0);         // ms
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const playStateRef = useRef(props.playPausePlayer);
  // Set when play is pressed before there is a source (onPlayWithoutSource):
  // playback starts, from any time sought meanwhile, once the source arrives.
  const startWhenLoadedRef = useRef(false);
  const pendingStartSecondsRef = useRef(0);

  const requestSourceForPlay = () => {
    if (props.source || !props.onPlayWithoutSource) return false;
    startWhenLoadedRef.current = true;
    props.onPlayWithoutSource();
    return true;
  };
  const volumeRef = useRef(volume);
  const [audioSpeed, setAudioSpeed] = useState(1);
  const [inputTime, setInputTime] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [subtitleFontSize, setSubtitleFontSize] = useState(() =>
    readStoredNumber("performancePlayer.subtitleFontSize", SUBTITLE_FONT_SIZE)
  );
  const [subtitleMaxWidth, setSubtitleMaxWidth] = useState(() =>
    readStoredNumber("performancePlayer.subtitleMaxWidth", SUBTITLE_MAX_WIDTH)
  );
  const subtitleStyle = {
    ...SUBTITLE_OVERLAY_STYLE,
    fontSize: `${subtitleFontSize}px`,
    maxWidth: `${subtitleMaxWidth}%`,
  };

  // Subtitle entry under the playhead; the original text shows at the top and
  // the translation at the bottom, as on the QA page.
  const activeSubtitle = useMemo(() => {
    if (!props.subtitles?.length) return null;
    const timeSec = currentTime / 1000;
    return (
      props.subtitles.find(
        (subtitle) =>
          timeSec >= parseTimestampAsTotalSeconds(subtitle.startTime) &&
          timeSec <= parseTimestampAsTotalSeconds(subtitle.endTime)
      ) || null
    );
  }, [currentTime, props.subtitles]);

  // Sorted dialogue start times (seconds) for the previous/next dialogue buttons.
  const dialogueStarts = useMemo(
    () =>
      (props.subtitles || [])
        .map((subtitle) => parseTimestampAsTotalSeconds(subtitle.startTime))
        .filter((start) => Number.isFinite(start))
        .sort((a, b) => a - b),
    [props.subtitles]
  );

  // Latest page-jump callback, so the debounced timer never calls a stale closure.
  const goToPageRef = useRef(props.goToPageAccordingTimeAPI);
  goToPageRef.current = props.goToPageAccordingTimeAPI;
  const onSkipRef = useRef(props.onSkip);
  onSkipRef.current = props.onSkip;
  const seekPageTimer = useRef(null);

  useEffect(() => () => clearTimeout(seekPageTimer.current), []);

  // A video taken out of the page keeps downloading until it is garbage
  // collected, so drop its source when the player goes away.
  useEffect(() => {
    const v = player.current;
    return () => {
      if (v) releaseVideo(v);
    };
  }, []);

  const handleTimeChange = useCallback((timeMs, fromSkipButton = false) => {
    const v = player.current;
    if (!v) return;
    const nextMs = clamp(Number(timeMs) || 0, 0, maxTime || 0);
    v.currentTime = nextMs / 1000;
    setCurrentTime(nextMs);

    // The slider fires continuously while dragging; load the dialogue page
    // around the new time only once the user settles on a position.
    clearTimeout(seekPageTimer.current);
    const goToPage =
      (fromSkipButton && onSkipRef.current) || goToPageRef.current;
    seekPageTimer.current = setTimeout(() => {
      goToPage?.(nextMs / 1000);
    }, SLIDER_PAGE_JUMP_DELAY_MS);
  }, [maxTime]);

  const skipMs = (props.skipSeconds ?? 0.1) * 1000;

  const goToPreviousDialogue = () => {
    const timeSec = currentTime / 1000;
    const previous = dialogueStarts
      .filter((start) => start < timeSec - DIALOGUE_RESTART_THRESHOLD_SEC)
      .pop();
    handleTimeChange((previous ?? 0) * 1000, true);
  };

  const goToNextDialogue = () => {
    const timeSec = currentTime / 1000;
    // Small margin so a jump that just landed on a start moves past it.
    const next = dialogueStarts.find((start) => start > timeSec + 0.05);
    if (next != null) handleTimeChange(next * 1000, true);
  };

  const onProgress = useCallback(() => {
    const v = player.current;
    const buf = v?.buffered;
    if (!v || !buf || buf.length === 0) return;
    const t = v.currentTime;
    let idx = 0;
    for (; idx < buf.length; idx++) {
      if (buf.start(idx) <= t && t <= buf.end(idx)) break;
    }
    const end = buf.end(Math.min(idx, buf.length - 1));
    setProgress((end || 0) * 1000);
  }, []);

  const onLoadedMeta = useCallback(() => {
    const v = player.current;
    setMaxTime((v?.duration || 0) * 1000);
    if (v?.videoWidth && v?.videoHeight) {
      props.onVideoSizeChange?.({ width: v.videoWidth, height: v.videoHeight });
    }
  }, [props.onVideoSizeChange]); // eslint-disable-line

  // Smooth UI time while playing
  useEffect(() => {
    let raf;
    const step = () => {
      const v = player.current;
      if (v) setCurrentTime(v.currentTime * 1000);
      raf = requestAnimationFrame(step);
    };
    if (isPlaying) raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [isPlaying]);

  // Reflect in parent
  useEffect(() => {
    props.displayAudioTime(((currentTime || 0) / 1000).toFixed(2));
  }, [currentTime]); // eslint-disable-line

  // External seek
  useEffect(() => {
    const v = player.current;
    if (!v) return;
    const tSec = props.seekTime ?? 0;
    pendingStartSecondsRef.current = tSec;
    v.currentTime = tSec;
    setCurrentTime(tSec * 1000);
  }, [props.seekTimeIndex]); // eslint-disable-line

  // Time input (masked)
  const handleTimeInputChange = (e) => {
    setInputTime(e.target.value);
    setIsEditing(true);
  };

  // Seek to the time in the timestamp field (the current time unless the user
  // typed one) and load the dialogue page around it, like releasing the slider.
  const goToInputTime = () => {
    const enteredMs = (parseTimestampAsTotalSeconds(inputTime) || 0) * 1000;
    const seekMs = clamp(enteredMs, 0, maxTime || 0);

    const v = player.current;
    if (!v) return;

    const wasPaused = v.paused;
    v.currentTime = seekMs / 1000;
    setCurrentTime(seekMs);
    setIsEditing(false);
    props.goToPageAccordingTimeAPI?.(seekMs / 1000);

    if (wasPaused) {
      v.pause();
      setIsPlaying(false);
    } else {
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      goToInputTime();
    }
  };

  useEffect(() => {
    if (!isEditing) {
      const formatted = new Date(currentTime).toISOString().substr(11, 12);
      setInputTime(formatted);
    }
  }, [currentTime, isEditing]);

  // Volume
  const handleVolumeChange = (val) => {
    const v = player.current;
    const newVolume = typeof val === "number" ? val : parseFloat(val || 0);
    const clamped = clamp(newVolume, 0, 1);
    if (v) v.volume = clamped;
    setVolume(clamped);
  };

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // Keep latest requested play state available for source reloads without
  // forcing the player to re-initialize whenever the parent toggles play/pause.
  useEffect(() => {
    playStateRef.current = props.playPausePlayer;
  }, [props.playPausePlayer]);

  // Reload the HTML5 video element only when a different project source is
  // provided so the newly selected media starts from the beginning without
  // wiping out manual seeks whenever the play/pause state changes.
  useEffect(() => {
    const v = player.current;
    if (!v) return;
    // Cleared source: stop the previous video downloading.
    if (!props.source) {
      releaseVideo(v);
      setCurrentTime(0);
      setProgress(0);
      setMaxTime(0);
      setIsPlaying(false);
      return;
    }

    const startWhenLoaded = startWhenLoadedRef.current;
    startWhenLoadedRef.current = false;
    const shouldAutoplay = playStateRef.current === "play" || startWhenLoaded;
    const startSeconds = startWhenLoaded ? pendingStartSecondsRef.current : 0;
    const startVolume = clamp(volumeRef.current, 0, 1);

    v.pause();
    v.load();
    v.currentTime = startSeconds;
    v.volume = startVolume;
    setCurrentTime(startSeconds * 1000);
    setProgress(0);
    setMaxTime(0);
    setIsPlaying(false);

    if (shouldAutoplay) {
      v
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  }, [props.source]);

  // Speed (re-enabled during sync; can also notify parent to set audio rate)
  const handleSpeedChange = (e) => {
    const v = player.current;
    const newSpeed = parseFloat(e.target.value);
    if (v) v.playbackRate = newSpeed;
    setAudioSpeed(newSpeed);
    if (props.syncPreview) {
      props.onSyncSetSpeed?.(newSpeed); // let parent apply to audio too
    }
  };

  // Auto-mute video when syncing (audio is master)
  useEffect(() => {
    handleVolumeChange(props.syncPreview ? 0 : 1);
    // eslint-disable-next-line
  }, [props.syncPreview]);

  // Pause the local video when parent requests a manual pause while unsynced
  useEffect(() => {
    if (props.syncPreview) return;
    if (props.manualPauseTick == null) return;

    const v = player.current;
    if (!v) return;

    v.pause();
    setIsPlaying(false);
  }, [props.manualPauseTick, props.syncPreview]);

  // Deterministic video play/pause while syncing (parent-driven)
  useEffect(() => {
    if (!props.syncPreview) return;
    const v = player.current;
    if (!v) return;
    if (props.playPausePlayer === "play") {
      if (requestSourceForPlay()) return;
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, [props.playPausePlayer, props.syncPreview]); // eslint-disable-line

  // Soft/Hard sync to audio master clock
  useEffect(() => {
    if (!props.syncPreview) return;
    if (props.masterMs == null) return;
    const v = player.current;
    if (!v) return;

    const videoMs = (v.currentTime || 0) * 1000;
    const drift = videoMs - props.masterMs;

    if (Math.abs(drift) > HARD_DRIFT_MS) {
      const now = performance.now();
      if (now - lastHardSyncAt.current > HARD_COOLDOWN_MS) {
        v.currentTime = props.masterMs / 1000;
        lastHardSyncAt.current = now;
        v.playbackRate = 1;
      }
      return;
    }

    if (Math.abs(drift) > SOFT_DRIFT_MS) {
      const correction = clamp(1 - drift / 800, 0.95, 1.05);
      if (!Number.isNaN(correction)) v.playbackRate = correction;
    } else {
      if (v.playbackRate !== audioSpeed) v.playbackRate = audioSpeed;
    }
  }, [props.masterMs, props.syncPreview, audioSpeed]);

  // Re-enabled: local toggle works in sync mode by delegating to parent
  const togglePlayPause = () => {
    const v = player.current;
    if (!v) return;

    if (props.syncPreview) {
      if (props.playPausePlayer === "play") {
        // ask parent to pause audio; video will follow via playPausePlayer
        props.onSyncPause?.();
      } else {
        // ask parent to play audio
        props.onSyncPlay?.();
      }
      return;
    }

    // Non-sync mode: control the video locally
    if (!isPlaying && requestSourceForPlay()) return;
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    }
  };

  const showPauseControl = props.syncPreview
    ? props.playPausePlayer === "play"
    : isPlaying;

  const handlePreviewChange = (event) => {
    const previewEnabled = event.target.checked;

    if (previewEnabled && isPlaying) {
      props.onSyncPlay?.();
    } else {
      props.onSyncPause?.();
    }

    props.setSyncPreview(previewEnabled);
  };

  return (
    <Grid
      item
      container
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      className={classes["AudioOutPutHorizontal"]}
      direction="row"
      width="100%"
    >
      <Grid
        item
        container
        className={classes["AudioOutPutVideoHorizontal"]}
        sx={{ position: "relative" }}
      >
        <video
          ref={setPlayerElement}
          width="100%"
          height="100%"
          preload="metadata"
          onLoadedMetadata={onLoadedMeta}
          onProgress={onProgress}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onWaiting={() => props.onSyncVideoWaiting?.()}
          onCanPlay={() => props.onSyncVideoReady?.()}
        >
          {props.source && <source src={props.source} type="video/mp4" />}
        </video>

        {props.showOriginalSubtitles && activeSubtitle?.originalText && (
          <div style={{ ...subtitleStyle, top: "5%" }}>
            {activeSubtitle.originalText}
          </div>
        )}

        {props.showSubtitles && activeSubtitle?.text && (
          <div style={{ ...subtitleStyle, bottom: "5%" }}>
            {activeSubtitle.text}
          </div>
        )}
      </Grid>

      <Grid
        item
        container
        className={classes["AudioOutPutController"]}
        paddingTop={0}
      >
        <Grid
          item
          paddingLeft={1}
          paddingRight={1}
          sx={{ width: "100%", height: 12 }}
          bgcolor="#EBEBEB"
          className={classes["VideoSeekSliderSlim"]}
        >
          <VideoSeekSlider
            max={maxTime}
            currentTime={currentTime}
            bufferTime={progress}
            onChange={handleTimeChange}
            limitTimeTooltipBySides={true}
            secondsPrefix="00:"
            minutesPrefix="0:"
          />
        </Grid>

        <Grid
          item
          container
          direction="row"
          alignItems="center"
          gap={1}
          marginLeft={1}
          paddingY={0.5}
          fontSize="14px"
        >
          {props.dialogueSkip && (
            <Grid
              item
              sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
              title="Previous dialogue"
            >
              <SkipPreviousIcon fontSize="medium" onClick={goToPreviousDialogue} />
            </Grid>
          )}

          <Grid
            item
            sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
            title={props.skipSeconds ? `Back ${props.skipSeconds} seconds` : undefined}
          >
            <FastRewindIcon
              fontSize="medium"
              onClick={() => handleTimeChange(currentTime - skipMs, true)}
            />
          </Grid>

          <Grid
            item
            sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
          >
            {showPauseControl ? (
              <PauseCircleIcon fontSize="medium" onClick={togglePlayPause} />
            ) : (
              <PlayCircleIcon fontSize="medium" onClick={togglePlayPause} />
            )}
          </Grid>

          <Grid
            item
            sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
            title={props.skipSeconds ? `Forward ${props.skipSeconds} seconds` : undefined}
          >
            <FastForwardIcon
              fontSize="medium"
              onClick={() => handleTimeChange(currentTime + skipMs, true)}
            />
          </Grid>

          {props.dialogueSkip && (
            <Grid
              item
              sx={{ color: `${primaryColor}`, cursor: "pointer", display: "flex" }}
              title="Next dialogue"
            >
              <SkipNextIcon fontSize="medium" onClick={goToNextDialogue} />
            </Grid>
          )}

          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            xs="auto"
            wrap="nowrap"
            gap={0.5}
          >
            <Grid item>
              <InputMask
                className={`${classes["VideoPlaceTimelineEditTranslation"]} ${classes["PlayerTimeInput"]}`}
                mask="99:99:99.99"
                value={inputTime}
                onChange={handleTimeInputChange}
                onKeyPress={handleKeyPress}
                placeholder="HH:MM:SS.MS"
              />
            </Grid>

            {/* Jump the dialogue list to the time beside it, like releasing the slider. */}
            {props.goToPageAccordingTimeAPI && (
              <Grid item>
                <button
                  type="button"
                  className={classes["GoToTimeButton"]}
                  onClick={goToInputTime}
                  title="Show the dialogues at this time"
                >
                  Go to
                </button>
              </Grid>
            )}
          </Grid>

          {!props.settingsMenu && (
            <>
              <Grid>Speed:</Grid>
              <Grid
                item
                container
                justifyContent="center"
                alignItems="center"
                marginRight={1}
                xs="auto"
              >
                <select
                  value={audioSpeed}
                  onChange={handleSpeedChange}
                  className={classes["AudioOutPutShowTimeOptionBox"]}
                // NOTE: no longer disabled during sync
                >
                  {PLAYBACK_SPEED_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Grid>
            </>
          )}

          {/* While previewing the video stays muted; this sets the dialogue
              listening volume instead. */}
          <Grid item sx={{ display: "flex" }}>
            <VolumeControl
              value={props.syncPreview ? props.previewVolume ?? 1 : volume}
              max={props.syncPreview ? 2 : 1}
              onChange={(val) =>
                props.syncPreview
                  ? props.onPreviewVolumeChange?.(val)
                  : handleVolumeChange(val)
              }
            />
          </Grid>

          {props.settingsMenu ? (
            <>
              <Grid
                item
                sx={{
                  marginLeft: "auto",
                  marginRight: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <button
                  type="button"
                  className={classes["PlayerPreviewButton"]}
                  aria-pressed={Boolean(props.syncPreview)}
                  title="Play each dialogue's generated audio with the video"
                  onClick={() =>
                    handlePreviewChange({ target: { checked: !props.syncPreview } })
                  }
                >
                  <GraphicEqIcon sx={{ fontSize: 18 }} />
                  Preview
                </button>
                <button
                  type="button"
                  className={classes["PlayerSettingsButton"]}
                  aria-label="Player settings"
                  aria-haspopup="true"
                  aria-expanded={Boolean(settingsAnchor)}
                  onClick={(event) => setSettingsAnchor(event.currentTarget)}
                >
                  <SettingsIcon fontSize="small" />
                </button>
              </Grid>

              <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                slotProps={{ paper: { className: classes["PlayerSettingsMenu"] } }}
              >
                <label className={classes["PlayerSettingsRow"]}>
                  <span>Continue preview across pages</span>
                  <Switch
                    size="small"
                    checked={props.continuePreviewAcrossPages}
                    onChange={(event) =>
                      props.setContinuePreviewAcrossPages(event.target.checked)
                    }
                  />
                </label>
                {props.toggleOriginalSubtitles && (
                  <label className={classes["PlayerSettingsRow"]}>
                    <span>{props.originalLanguageDisplay} subtitles</span>
                    <Switch
                      size="small"
                      checked={Boolean(props.showOriginalSubtitles)}
                      onChange={props.toggleOriginalSubtitles}
                    />
                  </label>
                )}
                {props.toggleTranslatedSubtitles && (
                  <label className={classes["PlayerSettingsRow"]}>
                    <span>{props.translatedLanguageDisplay} subtitles</span>
                    <Switch
                      size="small"
                      checked={Boolean(props.showSubtitles)}
                      onChange={props.toggleTranslatedSubtitles}
                    />
                  </label>
                )}
                {props.subtitles && (
                  <>
                    <div className={classes["PlayerSettingsSliderRow"]}>
                      <span className={classes["PlayerSettingsSliderLabel"]}>
                        <span id="subtitle-size-label">Subtitle size</span>
                        <span>{subtitleFontSize}px</span>
                      </span>
                      <Slider
                        size="small"
                        value={subtitleFontSize}
                        min={SUBTITLE_FONT_SIZE.min}
                        max={SUBTITLE_FONT_SIZE.max}
                        step={1}
                        aria-labelledby="subtitle-size-label"
                        onChange={(_, value) => {
                          setSubtitleFontSize(value);
                          storeNumber("performancePlayer.subtitleFontSize", value);
                        }}
                      />
                    </div>
                    <div className={classes["PlayerSettingsSliderRow"]}>
                      <span className={classes["PlayerSettingsSliderLabel"]}>
                        <span id="subtitle-width-label">Subtitle max width</span>
                        <span>{subtitleMaxWidth}%</span>
                      </span>
                      <Slider
                        size="small"
                        value={subtitleMaxWidth}
                        min={SUBTITLE_MAX_WIDTH.min}
                        max={SUBTITLE_MAX_WIDTH.max}
                        step={5}
                        aria-labelledby="subtitle-width-label"
                        onChange={(_, value) => {
                          setSubtitleMaxWidth(value);
                          storeNumber("performancePlayer.subtitleMaxWidth", value);
                        }}
                      />
                    </div>
                  </>
                )}
                <label className={classes["PlayerSettingsRow"]}>
                  <span>Playback speed</span>
                  <select
                    value={audioSpeed}
                    onChange={handleSpeedChange}
                    className={classes["PlayerSettingsSelect"]}
                  >
                    {PLAYBACK_SPEED_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </Popover>
            </>
          ) : (
            <>
            <Grid item xs={1}>
              <label style={{ whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={props.syncPreview}
                  onChange={handlePreviewChange}
                />
                {" "}
                <span>Preview</span>
              </label>
            </Grid>

            <Grid item xs="auto">
              <label style={{ whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={props.continuePreviewAcrossPages}
                  onChange={(event) =>
                    props.setContinuePreviewAcrossPages(event.target.checked)
                  }
                />
                {" "}
                <span>Continue Across Pages</span>
              </label>
            </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PerformanceVideoPlayer;
